import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, Sun, Menu, X, BookOpen, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";

export function Navbar() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const doLogout = async () => {
        await logout();
        navigate("/");
    };

    const links = [
        { to: "/editors", label: "Trova un editor" },
        { to: "/#come-funziona", label: "Come funziona" },
    ];

    return (
        <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
                    <BookOpen className="text-primary" size={22} strokeWidth={1.8} />
                    <span className="font-serif text-2xl font-semibold tracking-tight">Incipit</span>
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <Link key={l.to} to={l.to} data-testid={`nav-${l.label}`}
                            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                            {l.label}
                        </Link>
                    ))}
                </div>

                <div className="hidden md:flex items-center gap-3">
                    <button onClick={toggle} data-testid="theme-toggle" aria-label="Cambia tema"
                        className="p-2 rounded-full hover:bg-secondary transition-colors">
                        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                    {user ? (
                        <>
                            {user.role === "admin" && (
                                <Link to="/admin">
                                    <Button variant="ghost" size="sm" data-testid="nav-admin" className="gap-2">
                                        <Shield size={16} /> Admin
                                    </Button>
                                </Link>
                            )}
                            <Link to="/dashboard">
                                <Button variant="ghost" size="sm" data-testid="nav-dashboard" className="gap-2">
                                    <LayoutDashboard size={16} /> Area personale
                                </Button>
                            </Link>
                            <Button variant="outline" size="sm" onClick={doLogout} data-testid="nav-logout" className="gap-2">
                                <LogOut size={16} /> Esci
                            </Button>
                        </>
                    ) : (
                        <>
                            <Link to="/auth?mode=login">
                                <Button variant="ghost" size="sm" data-testid="nav-login">Accedi</Button>
                            </Link>
                            <Link to="/auth?mode=register">
                                <Button size="sm" data-testid="nav-register" className="bg-primary hover:bg-primary/90">Registrati</Button>
                            </Link>
                        </>
                    )}
                </div>

                <div className="md:hidden flex items-center gap-2">
                    <button onClick={toggle} aria-label="Cambia tema" className="p-2 rounded-full hover:bg-secondary">
                        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                    <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" className="p-2 rounded-full hover:bg-secondary">
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </nav>

            {open && (
                <div className="md:hidden border-t border-border bg-background px-4 py-4 flex flex-col gap-3 animate-fade-in">
                    {links.map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm font-medium py-1">{l.label}</Link>
                    ))}
                    <div className="h-px bg-border my-1" />
                    {user ? (
                        <>
                            {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium py-1">Admin</Link>}
                            <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium py-1">Area personale</Link>
                            <button onClick={() => { setOpen(false); doLogout(); }} className="text-sm font-medium py-1 text-left">Esci</button>
                        </>
                    ) : (
                        <div className="flex gap-3">
                            <Link to="/auth?mode=login" onClick={() => setOpen(false)} className="flex-1"><Button variant="outline" className="w-full">Accedi</Button></Link>
                            <Link to="/auth?mode=register" onClick={() => setOpen(false)} className="flex-1"><Button className="w-full bg-primary">Registrati</Button></Link>
                        </div>
                    )}
                </div>
            )}
        </header>
    );
}
